import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getWorkspaces } from './api';
import { setWorkspaceIdForApi, clearWorkspaceIdForApi } from './api';
import { getToken, AUTH_TOKEN_CHANGED_EVENT } from './auth';

const WORKSPACE_STORAGE_KEY = 'eagle_workspace_id';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setCurrentWorkspace = useCallback((ws) => {
    if (ws) {
      setCurrentWorkspaceState(ws);
      setWorkspaceIdForApi(ws.id);
      try {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, ws.id);
      } catch (_) {}
    } else {
      setCurrentWorkspaceState(null);
      clearWorkspaceIdForApi();
      try {
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      } catch (_) {}
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await getWorkspaces();
      const list = res.data?.workspaces || [];
      setWorkspaces(list);
    } catch (_) {
      setWorkspaces([]);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!getToken()) {
        setWorkspaces([]);
        setCurrentWorkspaceState(null);
        clearWorkspaceIdForApi();
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getWorkspaces();
        const list = res.data?.workspaces || [];
        setWorkspaces(list);
        const savedId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
        const selected = list.find((w) => w.id === savedId) || list[0] || null;
        if (selected) {
          setCurrentWorkspaceState(selected);
          setWorkspaceIdForApi(selected.id);
          try {
            localStorage.setItem(WORKSPACE_STORAGE_KEY, selected.id);
          } catch (_) {}
        } else {
          setCurrentWorkspaceState(null);
          clearWorkspaceIdForApi();
        }
      } catch (_) {
        setWorkspaces([]);
        setCurrentWorkspaceState(null);
        clearWorkspaceIdForApi();
      } finally {
        setLoading(false);
      }
    };
    load();
    const handler = () => load();
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, handler);
    return () => window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, handler);
  }, []);

  const value = {
    workspaces,
    currentWorkspace,
    setCurrentWorkspace,
    refreshWorkspaces,
    loading,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
